import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Collapse,
  Divider,
  Alert,
} from '@mui/material';
import { ExpandMore, ExpandLess, Favorite } from '@mui/icons-material';

interface Props {
  open: boolean;
  packageName: string;
  price: number;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const PurchaseConsentPopup: React.FC<Props> = ({
  open,
  packageName,
  price,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleClose = () => {
    setConfirmed(false);
    setDetailOpen(false);
    onCancel();
  };

  const handleConfirm = () => {
    if (!confirmed) return;
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="purchase-consent-title"
      // 시니어 UX: 바깥 클릭으로 닫히지 않음 (실수 방지)
      disableEscapeKeyDown={false}
    >
      <DialogTitle
        id="purchase-consent-title"
        sx={{ textAlign: 'center', pt: 3, pb: 1, fontWeight: 700, fontSize: '1.2rem' }}
      >
        <Favorite sx={{ color: '#D94F6E', mr: 1, verticalAlign: 'middle' }} />
        하트 구매 안내
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {/* 핵심 안내 문구 */}
        <Typography
          sx={{ fontSize: '1rem', lineHeight: 1.8, color: 'text.primary', textAlign: 'center', mb: 2 }}
        >
          하트는 디지털 콘텐츠로,{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>
            사용 후에는 환불이 제한
          </Box>
          됩니다.
          <br />
          미사용 하트는 환불 가능합니다.
        </Typography>

        {/* 상세 보기 토글 */}
        <Button
          size="small"
          onClick={() => setDetailOpen((v) => !v)}
          endIcon={detailOpen ? <ExpandLess /> : <ExpandMore />}
          sx={{ fontSize: '0.875rem', color: 'primary.main', mb: 1, display: 'flex', mx: 'auto' }}
        >
          상세 보기
        </Button>

        <Collapse in={detailOpen}>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.9rem', '& .MuiAlert-message': { width: '100%' } }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              환불 안내
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.5, fontSize: '0.875rem' } }}>
              <li>구매 후 7일 이내, 미사용 시 전액 환불 가능</li>
              <li>일부 사용 후에도 미사용 하트는 환불 가능 (수수료 10% 이내)</li>
              <li>사용된 하트는 환불 불가 (전자상거래법 제17조)</li>
              <li>리워드(무료) 하트는 환불 대상 아님</li>
              <li>환불 요청: 고객센터 (3영업일 이내 처리)</li>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              자세한 사항은 서비스 이용약관 제14조를 참고하세요.
            </Typography>
          </Alert>
        </Collapse>

        <Divider sx={{ my: 2 }} />

        {/* 확인 체크박스 */}
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 28 },
                padding: '10px',
              }}
              inputProps={{ 'aria-label': '구매 안내 확인' }}
            />
          }
          label={
            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
              확인했습니다
            </Typography>
          }
          sx={{ mx: 0 }}
        />

        {/* 구매 패키지 안내 */}
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
            {packageName}
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'primary.main' }}>
            ₩{price.toLocaleString('ko-KR')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        {/* 결제 버튼 — 체크박스 미체크 시 비활성화 */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleConfirm}
          disabled={!confirmed || loading}
          sx={{
            py: 1.8,
            fontSize: '1rem',
            fontWeight: 700,
            bgcolor: confirmed ? '#0D1F3C' : undefined,
            '&:hover': { bgcolor: '#1A3461' },
          }}
        >
          {loading ? '처리 중...' : `₩${price.toLocaleString('ko-KR')} 결제하기`}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={handleClose}
          disabled={loading}
          sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
        >
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseConsentPopup;
